
import { AppState, PetCollection, PetCollectionItem, PetInventoryItem } from '@/types';
import { AppAction } from '../types';
import { 
  createEmptyCollection, 
  hatchPet, 
  feedPet, 
  evolvePetToMount, 
  generateActivityDrops,
  FOOD_ITEMS 
} from '@/lib/petCollectionSystem';

// Handle hatching a pet from egg + potion
export const handleHatchPet = (state: AppState, action: Extract<AppAction, { type: 'HATCH_PET' }>): AppState => {
  if (!state.petCollection || !state.user) {
    return state;
  }

  const { eggId, potionId } = action.payload;
  const inventory = state.petCollection.inventory;
  
  // Find egg and potion in inventory
  const eggItem = inventory.find(item => item.item_type === 'egg' && item.id === eggId);
  const potionItem = inventory.find(item => item.item_type === 'potion' && item.id === potionId);

  if (!eggItem || !potionItem || eggItem.quantity < 1 || potionItem.quantity < 1) {
    return state;
  }

  // Create new pet
  const newPet: PetCollectionItem = {
    id: `pet_${Date.now()}`,
    user_id: state.user.id,
    species_id: `species_${eggItem.item_key}_${potionItem.item_key}`,
    species: {
      id: `species_${eggItem.item_key}_${potionItem.item_key}`,
      name: `${potionItem.item_key} ${eggItem.item_key}`,
      type: 'pet',
      base_species: eggItem.item_key,
      color: potionItem.item_key,
      rarity: 'common',
      unlock_requirements: {},
      bonuses: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    egg_id: eggItem.id,
    potion_id: potionItem.id,
    type: 'pet',
    food_fed: 0,
    food_needed: 50,
    obtained: true,
    obtained_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  // Update inventory - decrease quantities
  const updatedInventory = inventory.map(item => {
    if (item.id === eggId) {
      return { ...item, quantity: item.quantity - 1 };
    }
    if (item.id === potionId) {
      return { ...item, quantity: item.quantity - 1 };
    }
    return item;
  }).filter(item => item.quantity > 0);

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    pets: [...state.petCollection.pets, newPet],
    inventory: updatedInventory
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle feeding a pet
export const handleFeedPetCollection = (state: AppState, action: Extract<AppAction, { type: 'FEED_PET_COLLECTION' }>): AppState => {
  if (!state.petCollection) {
    return state;
  }

  const { petId, foodKey, quantity } = action.payload;
  const inventory = state.petCollection.inventory;
  
  // Find food item in inventory
  const foodItem = inventory.find(item => item.item_type === 'food' && item.item_key === foodKey);
  if (!foodItem || foodItem.quantity < quantity) {
    return state;
  }

  // Find pet to feed
  const petIndex = state.petCollection.pets.findIndex(pet => pet.id === petId);
  if (petIndex === -1) {
    return state;
  }

  const pet = state.petCollection.pets[petIndex];
  if (pet.type === 'mount') {
    return state; // Can't feed mounts
  }

  // Feed the pet
  const updatedPet = feedPet(pet, foodKey, quantity);

  // Update inventory
  const updatedInventory = inventory.map(item => {
    if (item.id === foodItem.id) {
      return { ...item, quantity: item.quantity - quantity };
    }
    return item;
  }).filter(item => item.quantity > 0);

  const updatedPets = [...state.petCollection.pets];
  updatedPets[petIndex] = updatedPet;

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    pets: updatedPets,
    inventory: updatedInventory
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle evolving pet to mount
export const handleEvolvePetToMount = (state: AppState, action: Extract<AppAction, { type: 'EVOLVE_PET_TO_MOUNT' }>): AppState => {
  if (!state.petCollection) {
    return state;
  }

  const { petId } = action.payload;
  const petIndex = state.petCollection.pets.findIndex(pet => pet.id === petId);
  
  if (petIndex === -1) {
    return state;
  }

  const pet = state.petCollection.pets[petIndex];
  if (pet.food_fed < pet.food_needed) {
    return state; // Not enough food fed
  }

  // Create mount version
  const mount: PetCollectionItem = {
    ...pet,
    type: 'mount',
    species: {
      ...pet.species,
      type: 'mount',
      name: pet.species.name + ' Mount',
      bonuses: {
        xp_multiplier: 1.1,
        bonus_type: 'speed'
      }
    },
    mount_unlocked_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  // Remove from pets, add to mounts
  const updatedPets = state.petCollection.pets.filter(p => p.id !== petId);
  const updatedMounts = [...state.petCollection.mounts, mount];

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    pets: updatedPets,
    mounts: updatedMounts
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle equipping a pet
export const handleEquipPet = (state: AppState, action: Extract<AppAction, { type: 'EQUIP_PET' }>): AppState => {
  if (!state.petCollection) {
    return state;
  }

  const { petId } = action.payload;
  const pet = state.petCollection.pets.find(p => p.id === petId);
  
  if (!pet) {
    return state;
  }

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    currentPet: pet.id
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle equipping a mount
export const handleEquipMount = (state: AppState, action: Extract<AppAction, { type: 'EQUIP_MOUNT' }>): AppState => {
  if (!state.petCollection) {
    return state;
  }

  const { mountId } = action.payload;
  const mount = state.petCollection.mounts.find(m => m.id === mountId);
  
  if (!mount) {
    return state;
  }

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    currentMount: mount
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle unequipping pet
export const handleUnequipPet = (state: AppState): AppState => {
  if (!state.petCollection) {
    return state;
  }

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    currentPet: undefined
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle unequipping mount
export const handleUnequipMount = (state: AppState): AppState => {
  if (!state.petCollection) {
    return state;
  }

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    currentMount: undefined
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle adding inventory item
export const handleAddInventoryItem = (state: AppState, action: Extract<AppAction, { type: 'ADD_INVENTORY_ITEM' }>): AppState => {
  if (!state.petCollection || !state.user) {
    return state;
  }

  const { itemType, itemKey, quantity } = action.payload;
  const inventory = state.petCollection.inventory;
  
  // Find existing item
  const existingItemIndex = inventory.findIndex(item => 
    item.item_type === itemType && item.item_key === itemKey
  );

  let updatedInventory;
  if (existingItemIndex !== -1) {
    // Update existing item quantity
    updatedInventory = inventory.map((item, index) => 
      index === existingItemIndex 
        ? { ...item, quantity: item.quantity + quantity }
        : item
    );
  } else {
    // Add new item
    const newItem: PetInventoryItem = {
      id: `inv_${Date.now()}`,
      user_id: state.user.id,
      item_type: itemType,
      item_key: itemKey,
      type: itemType,
      quantity,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    updatedInventory = [...inventory, newItem];
  }

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    inventory: updatedInventory
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle removing inventory item
export const handleRemoveInventoryItem = (state: AppState, action: Extract<AppAction, { type: 'REMOVE_INVENTORY_ITEM' }>): AppState => {
  if (!state.petCollection) {
    return state;
  }

  const { itemId, quantity } = action.payload;
  const inventory = state.petCollection.inventory;
  
  const updatedInventory = inventory.map(item => {
    if (item.id === itemId) {
      return { ...item, quantity: Math.max(0, item.quantity - quantity) };
    }
    return item;
  }).filter(item => item.quantity > 0);

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    inventory: updatedInventory
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};

// Handle initializing pet collection
export const handleInitializePetCollection = (state: AppState): AppState => {
  if (state.petCollection) {
    return state; // Already initialized
  }

  // Create a fallback user ID if no user exists (for testing/demo purposes)
  const userId = state.user?.id || 'demo-user-' + Date.now();

  return {
    ...state,
    petCollection: createEmptyCollection(userId)
  };
};

// Handle loading pet collection from database
export const handleLoadPetCollection = (state: AppState, action: Extract<AppAction, { type: 'LOAD_PET_COLLECTION' }>): AppState => {
  const { collection } = action.payload;
  
  return {
    ...state,
    petCollection: collection
  };
};

// Handle activity completion with pet item drops
export const handleActivityCompletionWithDrops = (state: AppState, activityPoints: number): AppState => {
  if (!state.petCollection || !state.user) {
    return state;
  }

  const drops = generateActivityDrops(activityPoints);
  
  if (drops.length === 0) {
    return state;
  }

  // Add drops to inventory
  let updatedInventory = [...state.petCollection.inventory];
  
  drops.forEach(drop => {
    const existingItemIndex = updatedInventory.findIndex(item => 
      item.item_type === drop.item_type && item.item_key === drop.item_key
    );

    if (existingItemIndex !== -1) {
      updatedInventory[existingItemIndex] = {
        ...updatedInventory[existingItemIndex],
        quantity: updatedInventory[existingItemIndex].quantity + drop.quantity
      };
    } else {
      updatedInventory.push({
        ...drop,
        id: `inv_${Date.now()}_${Math.random()}`,
        user_id: state.user.id
      });
    }
  });

  const updatedCollection: PetCollection = {
    ...state.petCollection,
    inventory: updatedInventory
  };

  return {
    ...state,
    petCollection: updatedCollection
  };
};
